<script setup lang="ts">
  import { computed, getCurrentInstance, nextTick, ref } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiField from '@/components/pos-v2/ui/PosUiField.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import PosUiPopover from '@/components/pos-v2/ui/PosUiPopover.vue';
  import { useCatalogPage } from '@/composition/catalog/catalog-page-context';
  import { useProductEditorContext } from '@/composition/catalog/use-product-editor';
  import { useConfirmDialog } from '@/composition/use-confirm-dialog';
  import {
    addSize,
    canAddSize,
    discountSummary,
    keptSize,
    moveSize,
    removeSize,
    toSingle,
    toSizes,
    updateSize,
  } from '@/domain/catalog/price-editing';
  import {
    IPriceForm,
    ISizeRowForm,
    MAX_SIZE_TITLE_LENGTH,
    MAX_SIZES,
  } from '@/domain/catalog/product-form';
  import { currencySymbol, formatMoney } from '@/domain/store-setup/money';
  import { nextDomId } from '@/utils/dom-id.utils';
  import V2CatalogEditorSection from '../shared/V2CatalogEditorSection.vue';
  import V2CatalogSizeRow from './V2CatalogSizeRow.vue';

  /**
   * «Precio» (plan §5.12): one price with an optional discount, or sizes
   * («Usar precios múltiples» turns the price into the first size; the
   * sizes ⋯ goes back to one price, keeping the first active size's).
   */
  const editor = useProductEditorContext();
  const { data, online } = useCatalogPage();
  const confirmDialog = useConfirmDialog();
  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));

  const root = ref<HTMLElement | null>(null);
  const priceId = nextDomId('product-price');
  const discountId = nextDomId('product-discount');
  const editingTitle = ref(false);

  const price = computed(() => editor.form.value?.price ?? null);
  const readOnly = computed(() => editor.readOnly.value);
  const symbol = computed(() => currencySymbol(data.rowContext.value.currency));
  const errors = computed(() => editor.visibleErrors.value);

  const ERRORS: Record<string, string> = {
    required: 'menuPage.price.required',
    invalid: 'menuPage.price.invalid',
    discountTooHigh: 'menuPage.price.discountTooHigh',
  };

  const priceError = computed(() =>
    errors.value.price ? t(ERRORS[errors.value.price]) : undefined
  );
  const discountError = computed(() =>
    errors.value.discount ? t(ERRORS[errors.value.discount]) : undefined
  );
  const sizesError = computed(() => {
    if (errors.value.sizes === 'noActive')
      return t('menuPage.editor.errors.noActiveSize');
    if (errors.value.sizes === 'none')
      return t('menuPage.editor.errors.noSize');
    if (errors.value.sizesTitle === 'required')
      return t('menuPage.editor.errors.sizesTitle');
    if (errors.value.sizesTitle === 'tooLong')
      return t('menuPage.editor.errors.tooLong', { n: MAX_SIZE_TITLE_LENGTH });
    return null;
  });

  const summary = computed(() => {
    const current = price.value;
    if (current?.mode !== 'single') return null;
    const discount = discountSummary(current.priceText, current.discountText);
    if (!discount) return null;
    return t('menuPage.editor.discountSummary', {
      before: formatMoney(
        discount.before,
        data.rowContext.value.currency,
        data.rowContext.value.country
      ),
      percent: discount.percent,
    });
  });

  function setPrice(next: IPriceForm) {
    editor.update('price', next);
  }

  async function focusSizeTitle(index: number) {
    await nextTick();
    root.value
      ?.querySelectorAll<HTMLInputElement>('.v2-catalog-size-row input')
      [index * 2]?.focus();
  }

  //#region Single

  function setSingle(patch: {
    priceText?: string;
    discountText?: string | null;
  }) {
    const current = price.value;
    if (current?.mode === 'single') setPrice({ ...current, ...patch });
  }

  function toggleDiscount() {
    const current = price.value;
    if (current?.mode !== 'single') return;
    setSingle({ discountText: current.discountText === null ? '' : null });
  }

  function startSizes() {
    const current = price.value;
    if (current?.mode !== 'single') return;
    setPrice(toSizes(current, t('menuPage.editor.sizesDefaultTitle')));
    void focusSizeTitle(0);
  }

  //#endregion

  //#region Sizes

  const sizes = computed(() =>
    price.value?.mode === 'sizes' ? price.value : null
  );

  function changeSize(key: string, patch: Partial<ISizeRowForm>) {
    if (sizes.value) setPrice(updateSize(sizes.value, key, patch));
  }

  function addRow() {
    if (!sizes.value) return;
    setPrice(addSize(sizes.value));
    void focusSizeTitle(sizes.value.rows.length);
  }

  function setTitle(title: string) {
    if (sizes.value) setPrice({ ...sizes.value, title });
  }

  async function backToSingle(close: () => void) {
    close();
    const current = sizes.value;
    if (!current) return;
    const kept = keptSize(current);
    const accepted = await confirmDialog.confirm({
      title: t('menuPage.editor.singleTitle'),
      message: kept
        ? t('menuPage.editor.singleMessage', {
            size: kept.title.trim() || t('menuPage.editor.size.untitled'),
            price: kept.priceText
              ? `${symbol.value} ${kept.priceText}`
              : t('menuPage.row.noPrice'),
          })
        : t('menuPage.editor.singleMessageEmpty'),
      acceptText: t('menuPage.editor.singleAccept'),
    });
    // The editor may have switched products meanwhile.
    if (accepted && sizes.value === current) setPrice(toSingle(current));
  }

  //#endregion
</script>

<template>
  <V2CatalogEditorSection
    v-if="price"
    class="v2-catalog-price-section"
    :title="t('menuPage.editor.price')"
  >
    <template v-if="sizes && !readOnly" #actions>
      <PosUiPopover placement="bottom" align="end">
        <template #trigger="{ toggle }">
          <PosUiButton
            color="neutral"
            variant="ghost"
            icon="dots-horizontal"
            :aria-label="t('menuPage.editor.sizesActions')"
            :title="t('menuPage.editor.sizesActions')"
            @click="toggle"
          />
        </template>
        <template #default="{ close }">
          <PosUiButton
            class="sizes-single"
            color="neutral"
            variant="ghost"
            icon="numeric-1-box-outline"
            block
            @click="backToSingle(close)"
            >{{ t('menuPage.editor.single') }}</PosUiButton
          >
        </template>
      </PosUiPopover>
    </template>

    <div ref="root" class="price-body">
      <!-- One price -->
      <template v-if="price.mode === 'single'">
        <div class="single-row">
          <PosUiField
            class="single-field"
            :label="t('menuPage.price.price')"
            :input-id="priceId"
            :error="priceError"
            :hint="priceError ? undefined : t('menuPage.editor.freeHint')"
          >
            <PosUiInput
              :id="priceId"
              :value="price.priceText"
              :prefix="symbol"
              inputmode="decimal"
              autocomplete="off"
              :disabled="readOnly"
              @input="setSingle({ priceText: $event })"
            />
          </PosUiField>
          <PosUiField
            v-if="price.discountText !== null"
            class="single-field"
            :label="t('menuPage.editor.discountPrice')"
            :input-id="discountId"
            :error="discountError"
            :hint="discountError ? undefined : summary || undefined"
          >
            <PosUiInput
              :id="discountId"
              :value="price.discountText"
              :prefix="symbol"
              inputmode="decimal"
              autocomplete="off"
              :disabled="readOnly"
              @input="setSingle({ discountText: $event })"
            />
          </PosUiField>
        </div>
        <template v-if="!readOnly">
          <label class="discount-toggle">
            <input
              type="checkbox"
              class="toggle-input"
              :checked="price.discountText !== null"
              @change="toggleDiscount"
            />
            {{ t('menuPage.editor.withDiscount') }}
          </label>
          <div class="sizes-offer">
            <p class="offer-text">{{ t('menuPage.editor.sizesOffer') }}</p>
            <PosUiButton
              class="offer-action"
              color="neutral"
              variant="outline"
              @click="startSizes"
              >{{ t('menuPage.editor.sizesOfferAction') }}</PosUiButton
            >
          </div>
        </template>
      </template>

      <!-- Sizes -->
      <template v-else-if="sizes">
        <div class="sizes-head" aria-hidden="true">
          <span class="head-size">{{ t('menuPage.editor.size.title') }}</span>
          <span class="head-price">{{ t('menuPage.price.price') }}</span>
        </div>
        <V2CatalogSizeRow
          v-for="(row, index) in sizes.rows"
          :key="row.key"
          :row="row"
          :currency-symbol="symbol"
          :errors="errors.sizeRows ? errors.sizeRows[row.key] || null : null"
          :first="index === 0"
          :last="index === sizes.rows.length - 1"
          :read-only="readOnly"
          :online="online"
          @change="changeSize(row.key, $event)"
          @move="setPrice(moveSize(sizes, row.key, $event))"
          @remove="setPrice(removeSize(sizes, row.key))"
        />
        <p v-if="sizesError" class="sizes-error">{{ sizesError }}</p>

        <div v-if="!readOnly" class="sizes-footer">
          <PosUiButton
            color="neutral"
            variant="ghost"
            icon="plus"
            :disabled="!canAddSize(sizes)"
            :title="
              canAddSize(sizes)
                ? undefined
                : t('menuPage.editor.maxSizes', { n: MAX_SIZES })
            "
            @click="addRow"
            >{{ t('menuPage.editor.addSize') }}</PosUiButton
          >
          <p class="sizes-hint">{{ t('menuPage.editor.sizesHint') }}</p>
        </div>

        <div class="sizes-title">
          <template v-if="editingTitle && !readOnly">
            <PosUiInput
              class="title-input"
              :value="sizes.title"
              :maxlength="MAX_SIZE_TITLE_LENGTH"
              :aria-label="t('menuPage.editor.sizesTitleLabel')"
              @input="setTitle"
              @enter="editingTitle = false"
            />
            <PosUiButton
              color="neutral"
              variant="ghost"
              @click="editingTitle = false"
              >{{ t('menuPage.select.done') }}</PosUiButton
            >
          </template>
          <template v-else>
            <span class="title-text">{{
              t('menuPage.editor.sizesTitleLine', { title: sizes.title })
            }}</span>
            <button
              v-if="!readOnly"
              type="button"
              class="title-change"
              @click="editingTitle = true"
            >
              {{ t('menuPage.editor.change') }}
            </button>
          </template>
        </div>
      </template>
    </div>
  </V2CatalogEditorSection>
</template>

<style scoped lang="scss">
  .v2-catalog-price-section {
    .sizes-single {
      @apply tw-justify-start tw-whitespace-nowrap;
    }

    // Margins, not flex gap (Chrome < 84).
    .single-row {
      @apply -tw-mx-1.5 tw-flex tw-flex-wrap;

      .single-field {
        @apply tw-mx-1.5 tw-mb-1 tw-min-w-0 tw-flex-1;
        flex-basis: 160px;
      }
    }

    .discount-toggle {
      @apply tw-mt-2 tw-inline-flex tw-min-h-[44px] tw-cursor-pointer tw-items-center tw-text-sm tw-text-neutral-700;

      .toggle-input {
        @apply tw-mr-2 tw-h-5 tw-w-5 tw-cursor-pointer;
        accent-color: rgb(var(--c-neutral-900));
      }
    }

    // Margins, not flex gap (Chrome < 84). On narrow widths the button
    // drops below the question.
    .sizes-offer {
      @apply tw-mt-3 tw-flex tw-flex-wrap tw-items-center tw-rounded-lg tw-bg-canvas tw-px-3 tw-py-2;

      .offer-text {
        @apply tw-my-1 tw-mr-3 tw-min-w-0 tw-flex-1 tw-text-sm tw-text-neutral-700;
        flex-basis: 200px;
      }

      .offer-action {
        @apply tw-my-1 tw-flex-none;
      }
    }

    .sizes-head {
      @apply tw-mb-1.5 tw-flex tw-pr-12 tw-text-xs tw-font-medium tw-text-neutral-500;

      .head-size {
        @apply tw-flex-1;
      }

      .head-price {
        @apply tw-ml-2 tw-w-32 tw-flex-none;
      }
    }

    .sizes-error {
      @apply tw-m-0 tw-mt-2 tw-text-xs tw-text-red-600;
    }

    .sizes-footer {
      @apply tw-mt-2;

      .sizes-hint {
        @apply tw-m-0 tw-mt-1 tw-text-xs tw-text-neutral-500;
      }
    }

    .sizes-title {
      @apply tw-mt-3 tw-flex tw-flex-wrap tw-items-center tw-border-0 tw-border-t tw-border-solid tw-border-neutral-100 tw-pt-3 tw-text-sm tw-text-neutral-600;

      .title-input {
        @apply tw-mr-2 tw-min-w-0 tw-flex-1;
      }

      .title-text {
        @apply tw-mr-2;
      }

      .title-change {
        @apply tw-cursor-pointer tw-rounded tw-border-0 tw-bg-transparent tw-p-0 tw-text-sm tw-font-semibold tw-text-neutral-900 tw-underline;

        &:focus-visible {
          @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
        }
      }
    }
  }
</style>
